import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import httpClient from 'superagent';
import { ChevronLink } from '@bibliocommons/base-links';
import { catalogBibShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import { FullScreenOverlayContainer } from 'app/components/shared/FullScreenOverlay';
import FullScreenOverlayActions from 'app/actions/FullScreenOverlayActions';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import NovelistOverlay from './NovelistOverlay';

import './MoreFromNovelist.scss';

const OVERLAY_KEY = 'more-from-novelist-overlay';
const NOVELIST_CONTENT_API_URL = 'https://novselect.ebscohost.com/Data/ContentByQuery';

export default function MoreFromNovelist({ catalogBib, heading, handleClick }) {
  const dispatch = useDispatch();
  const bibEntities = useSelector(selectBibEntities);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const [contentAvailable, setContentAvailable] = useState(false);

  const isbn = bibEntities.getIn([catalogBib.get('metadataId'), 'briefInfo', 'isbns', 0]);
  const novelistProfile = currentLibrary.getIn(['novelistParameters', 'profile']);
  const novelistPassword = currentLibrary.getIn(['novelistParameters', 'password']);

  useEffect(() => {
    let req;
    // Determine if NoveList has any interesting content for the isbn, before displaying the component.
    if (isbn && novelistProfile && novelistPassword) {
      req = httpClient
        .get(NOVELIST_CONTENT_API_URL)
        .query({
          profile: novelistProfile,
          password: novelistPassword,
          ClientIdentifier: isbn,
          ISBN: isbn
        })
        .then(response => {
          if (!req.aborted) {
            const data = JSON.parse(response.text);
            const interestingFeatures = Object.keys(data.FeatureContent || {}).filter(
              type => !['EResourceRecommendation', 'GoodReads'].includes(type)
            );
            setContentAvailable(!!interestingFeatures.length);
          }
        });
    }

    return () => {
      if (req) req.aborted = true;
    };
  }, [isbn, novelistProfile, novelistPassword]);

  function openOverlay() {
    dispatch(FullScreenOverlayActions.openOverlay(OVERLAY_KEY));
  }

  function closeOverlay() {
    dispatch(FullScreenOverlayActions.closeOverlay(OVERLAY_KEY));
  }

  function handleOpen() {
    openOverlay();
    if (handleClick) handleClick();
  }

  if (isbn && novelistProfile && novelistPassword && contentAvailable) {
    return (
      <div className="cp-more-from-novelist">
        {heading && <div className="heading">{heading}</div>}
        <ChevronLink href="#novelist-overlay" dataKey="more-from-novelist-link" handleClick={handleOpen} preventDefault>
          <span aria-hidden>
            <FormattedMessage id="view_all_from_novelist" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage id="sr_view_all_from_novelist" />
          </ScreenReaderMessage>
        </ChevronLink>
        <FullScreenOverlayContainer
          className="novelist-overlay"
          dataKey="novelist-overlay"
          overlayKey={OVERLAY_KEY}
          handleClose={closeOverlay}
        >
          <NovelistOverlay catalogBib={catalogBib} isbn={isbn} />
        </FullScreenOverlayContainer>
      </div>
    );
  }

  return null;
}

MoreFromNovelist.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  heading: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  handleClick: PropTypes.func
};
